import { useState, useEffect, ChangeEventHandler, useMemo, MutableRefObject, Dispatch, SetStateAction } from 'react';
import { createPortal } from 'react-dom';
import { t } from 'i18next';
import { Icon } from '../Icon';
import styles from './AutoComplete.module.scss';
import { StateRef } from '@siberiacancode/reactuse';
import clsx from 'clsx';

export interface SelectedOptionI {
  value?: string;
  label?: string | JSX.Element;
  placeholder?: string;
  type?: string;
}
interface AutoCompleteProps {
  staticSearch: boolean;
  id: string;
  options?: SelectedOptionI[] | null;
  optionsList?: Array<{ title: string; type: string; options: SelectedOptionI[] }> | null;
  loading?: boolean;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  divRef?: StateRef<HTMLDivElement>;
  inputRef: MutableRefObject<HTMLInputElement | null>;
  onSelect?: (selectedOption?: SelectedOptionI | null) => void;
  initialValue?: string;
  placeholder: string;
  onChange?: Dispatch<SetStateAction<string>>;
  hasmore?: boolean;
  isSuccess?: boolean;
  className?: string;
}

export const AutoComplete = ({
  className,
  hasmore,
  id,
  initialValue,
  onChange,
  onSelect,
  inputRef,
  divRef,
  options,
  optionsList,
  loading,
  onClose,
  onOpen,
  placeholder,
  open,
  staticSearch,
  isSuccess,
}: AutoCompleteProps) => {
  const [value, setValue] = useState<string | undefined>(initialValue);
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);
  const mainContentEl = document.getElementById('main');

  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  const suggestions = useMemo(() => {
    if (optionsList && staticSearch) {
      return optionsList
        .flatMap((list) => list.options)
        ?.filter(
          (option) => typeof option.label === 'string' && option.label?.toLowerCase().includes(value ? value.toLowerCase() : ''),
        );
    } else return options;
  }, [staticSearch, options, optionsList, value]);

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const target = event.target.value as string;
    setValue(target);
    onChange?.(target);
    if (!event.target.value) {
      onSelect?.(null);
    }
  };

  const handleSuggestionClick = (suggestion: { value?: string; label?: string | JSX.Element; placeholder?: string }) => {
    setValue(suggestion.placeholder);
    onSelect?.(suggestion);
  };

  useEffect(() => {
    if (inputRef.current) {
      const parentRect = inputRef.current.parentElement?.getBoundingClientRect();
      const inputRect = inputRef.current?.getBoundingClientRect();
      setPosition({
        top: inputRect.bottom + window.scrollY,
        left: parentRect?.left || inputRect?.left + window.scrollX,
        width: parentRect?.width || inputRect.width,
      });
    }
  }, [open, inputRef?.current]);

  useEffect(() => {
    const handleClickOutside = (event: { target: any }) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const suggestionsList = useMemo(() => {
    if (optionsList) {
      return optionsList?.map((group, i) => {
        const filteredSuggestions = suggestions?.filter((sug) => sug?.type === group.type);
        return (
          <li key={`${group.title}-${i}`} className={styles.listUp}>
            <span className={styles.list_title}>{group.title}</span>
            <ul className={styles.sublist}>
              {filteredSuggestions && filteredSuggestions?.length > 0 ? (
                filteredSuggestions?.map((el, i) => (
                  <li
                    id={`autocomplete-option-${el.value}-${i}`}
                    key={`suggestion-${el.value}-${i}`}
                    className={styles.list_item}
                    onMouseDown={() => {
                      handleSuggestionClick(el);
                    }}
                    data-value={el.value}
                  >
                    {el.label}
                  </li>
                ))
              ) : (
                <li className={styles.list_item} aria-disabled={true}>
                  {t('NoResultsFound')}
                </li>
              )}
            </ul>
          </li>
        );
      });
    } else if (options) {
      if (suggestions && suggestions?.length > 0) {
        return suggestions?.map((suggestion, i) => (
          <li
            id={`autocomplete-option-${suggestion.value}-${i}`}
            className={styles.list_item}
            onMouseDown={() => {
              handleSuggestionClick(suggestion);
            }}
            data-value={suggestion.value}
            key={`suggestion-${suggestion.value}-${i}`}
          >
            {suggestion.label}
          </li>
        ));
      }
    }
  }, [suggestions]);
  return (
    <>
      <div
        className={clsx(styles.input_container, className)}
        onClick={(e) => {
          inputRef.current?.focus();
          if (open) {
            if ('id' in e.target && e.target.id !== id) {
              onClose();
            }
          } else {
            onOpen();
          }
          inputRef?.current?.select();

          e.stopPropagation();
        }}
      >
        <input
          autoComplete="false"
          ref={inputRef}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          className={styles.input}
          id={id}
        />
        <div className={styles.chevron_button} data-open={open} id="chevron_icon">
          <Icon name="chevron-down-small" size="14px" color="var(--default-primary)" />
        </div>
      </div>
      {mainContentEl &&
        open &&
        createPortal(
          <ul
            id={`${id}-portal`}
            className={styles.list}
            style={{
              boxSizing: 'border-box',
              position: 'absolute',
              top: position.top,
              left: position.left,
              width: position.width,
              border: '1px solid transparent',
              backgroundColor: 'var(--search-background)',
            }}
          >
            {suggestionsList}
            <li className={styles.list_item} aria-disabled="true">
              {loading ? (
                <>{t('DataIsLoading')}</>
              ) : hasmore && suggestions?.length && suggestions?.length >= 10 && divRef ? (
                <div ref={divRef} className={styles.list_item} aria-disabled="true">
                  {t('LoadMore')}
                </div>
              ) : (
                isSuccess && suggestions && suggestions?.length === 0 && <> {t('NoResultsFound')}</>
              )}
            </li>
          </ul>,
          mainContentEl,
        )}
    </>
  );
};
